import { motion } from "framer-motion";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Phone } from "lucide-react";
import { mockContacts } from "../data/mockContacts";
import { useApp } from "../context/AppContext";
import Avatar from "../components/Avatar";
import ScreenHeader from "../components/ScreenHeader";

export const RecipientScreen = () => {
  const nav = useNavigate();
  const { updateDraft } = useApp();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return mockContacts;
    return mockContacts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.phone.replace(/\s/g, "").includes(q.replace(/\s/g, ""))
    );
  }, [query]);

  const isPhoneQuery = /^[+\d\s-]{4,}$/.test(query.trim());

  const selectContact = (contact) => {
    updateDraft({ recipient: contact });
    nav("/send/amount");
  };

  const useCustomPhone = () => {
    const phone = query.trim();
    if (!phone) return;
    const initials = "##";
    const contact = {
      id: "manual-" + Date.now(),
      name: phone,
      phone,
      initials,
      color: "#E5E7EB",
    };
    updateDraft({ recipient: contact });
    nav("/send/amount");
  };

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ type: "spring", bounce: 0, duration: 0.35 }}
      className="h-full flex flex-col"
      data-testid="recipient-screen"
    >
      <ScreenHeader title="Send to" testId="recipient-back-btn" />

      <div className="px-6 pb-3">
        <h1 className="font-display text-[28px] font-bold leading-tight tracking-tight dark:text-white">
          Who are you
          <br />
          sending to?
        </h1>
      </div>

      <div className="px-6 pt-2">
        <div className="relative">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            strokeWidth={2.5}
          />
          <input
            data-testid="recipient-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Phone number or name"
            inputMode="tel"
            className="w-full bg-gray-100 dark:bg-white/10 rounded-full h-12 pl-11 pr-5 font-medium outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10 transition-all text-sm dark:text-white"
          />
        </div>
      </div>

      {isPhoneQuery && query.trim() && (
        <div className="px-6 mt-4">
          <button
            data-testid="use-custom-phone-btn"
            onClick={useCustomPhone}
            className="w-full flex items-center gap-3 p-3 rounded-2xl bg-gray-900 text-white hover:bg-gray-800 transition-colors"
          >
            <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="text-left">
              <div className="text-[13px] font-semibold">Send to {query.trim()}</div>
              <div className="text-[11px] text-gray-400">New phone number</div>
            </div>
          </button>
        </div>
      )}

      <div className="px-6 mt-6 flex-1 overflow-y-auto phone-scroll">
        <div className="text-[11px] uppercase tracking-widest font-bold text-gray-400 mb-3">
          Your contacts
        </div>
        <div className="space-y-1">
          {filtered.map((c) => (
            <button
              key={c.id}
              data-testid={`contact-${c.id}`}
              onClick={() => selectContact(c)}
              className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-2xl transition-colors text-left"
            >
              <Avatar initials={c.initials} color={c.color} size={44} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14px] text-gray-900 dark:text-white truncate">
                  {c.name}
                </div>
                <div className="text-[12px] text-gray-500 dark:text-gray-400 truncate">{c.phone}</div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-center text-sm text-gray-500 py-8">
              No contacts match "{query}"
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default RecipientScreen;
